import { action, computed, makeAutoObservable, observable } from 'mobx';
import { FilterOptionType, FilterValueType } from 'src/components/RealityCheckForm/CustomDropdown';
import { StaffAccountBasicDataType } from 'src/AppState/sdk/staff';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { DateTime } from 'src_common/utils/time/time';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { z } from 'zod';
import { depositRestriction } from 'src_server/types/accountData';

const defValue = 1;

export const DEPOSIT_RESTRICTION_OPTIONS_ARRAY: FilterOptionType<number | null>[] = [
    {
        value: defValue,
        label: '1 hour',
    },
    {
        value: 24,
        label: '24 hours',
    },
    {
        value: 48,
        label: '48 hours',
    },
    {
        value: 72,
        label: '72 hours',
    },
    {
        value: 168,
        label: '1 week',
    },
    {
        value: null,
        label: 'permanent',
    },
];

export class DepositRestrictionState {
    @observable public selectedPeriod: number | null = defValue;
    @observable public isLoading: boolean = false;
    @observable public isError: boolean = false;

    public constructor(
        private readonly trpcClient: TrpcClient,
        private readonly customerId: number,
        private readonly models: ModelsState
    ) {
        this.selectedPeriod = DEPOSIT_RESTRICTION_OPTIONS_ARRAY[0]?.value ?? null;
        makeAutoObservable(this);
    }

    @computed private get basicDataUserModel(): StaffAccountBasicDataType | null {
        return this.models.getUserModel(this.customerId).basicData.data;
    }

    @computed public get getDepositRestriction(): Exclude<StaffAccountBasicDataType['depositRestriction'], undefined> {
        return this.basicDataUserModel?.depositRestriction ?? null;
    }

    @computed public get getSourceReason(): String {
        if (this.getDepositRestriction === null) {
            return 'none';
        }

        return this.getDepositRestriction.source
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }
    @computed public get getSourceDescription(): string | null {
        return this.getDepositRestriction?.description ?? null;
    }

    @computed public get getPermanent(): String {
        if (this.getDepositRestriction === null) {
            return 'none';
        }

        return this.getDepositRestriction.permanent === true ? 'true' : 'false';
    }

    @computed public get getEndDate(): String {
        if (this.getDepositRestriction === null) {
            return 'none';
        }

        if (this.getDepositRestriction.permanent === true) {
            return 'Permanent';
        }

        if (this.getDepositRestriction.expiryDate === null || this.getDepositRestriction.expiryDate === undefined) {
            return 'none';
        }

        return DateTime.from(this.getDepositRestriction.expiryDate)?.format('DD/MM/YYYY HH:mm:ss') ?? '';
    }

    @computed private get mapDepositRestrictionData(): depositRestriction.InputSchemaType['body'] {
        if (this.selectedPeriod === null) {
            return {
                permanent: true,
                updatedAt: new Date().toISOString(),
                expiryDate: null,
                description: 'Manual trigger',
                source: 'backoffice',
            };
        }
        const pendingSuspensionTime = new Date();
        pendingSuspensionTime.setHours(pendingSuspensionTime.getHours() + this.selectedPeriod);

        return {
            permanent: false,
            updatedAt: new Date().toISOString(),
            expiryDate: pendingSuspensionTime.toISOString(),
            description: 'Manual trigger',
            source: 'backoffice',
        };
    }

    @action public onSetDepositRestriction = async (): Promise<void> => {
        this.isLoading = true;
        this.isError = false;

        try {
            await this.trpcClient.client.accountData.patchDepositRestriction.mutate({
                accountId: this.customerId,
                body: this.mapDepositRestrictionData,
            });

            this.isLoading = false;
        } catch (e) {
            this.isLoading = false;
            this.isError = true;
        }
    };

    @action public onRemoveDepositRestriction = async (): Promise<void> => {
        try {
            const now = new Date().toISOString();
            await this.trpcClient.client.accountData.patchDepositRestriction.mutate({
                accountId: this.customerId,
                body: {
                    permanent: false,
                    updatedAt: this.getDepositRestriction?.updatedAt ?? now,
                    expiryDate: now,
                    description: 'Manual trigger',
                    source: 'backoffice',
                },
            });

            this.isLoading = false;
        } catch (e) {
            this.isLoading = false;
            this.isError = true;
        }
    };

    public onUpdatePeriod = (changedValue: FilterValueType): void => {
        const parsedType = z.number().nullable().safeParse(changedValue);

        if (parsedType.success === false) {
            console.error('Wrong period data type');
            return;
        }

        this.selectedPeriod = parsedType.data;
    };
}
